/**
 * Written Staffing Plan help — same topics as Listen (staffing-plan-help).
 * Table nomenclature follows the 6.x spec (P6 extracts → manpower → hiring → difference).
 */
import React from 'react';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';

export const STAFFING_GUIDE_ID = 'staffing-plan-help';
const ACCENT = '#0f766e';

const TABLE_MAP = [
  { name: 'Baseline — Hrs', src: 'P6 baseline resource assignment hours. Baseline = the schedule you pick on the tab (Current by default, or the Budget / proposal XER — exact instance from Filters → Budget / Current).' },
  { name: 'Baseline — manpower', src: 'Baseline Hrs ÷ the P6 calendar(s). Every resource divides by its own calendar week hours from P6 — 40, 50, 60 — not one global number.' },
  { name: 'Earned + Remaining — Hrs', src: 'Current schedule, one table: Actual Units (earned) up to the data date, then Remaining Units after it. When earned ends, remaining starts. Remaining is scaled by the FAC formula from Filters.' },
  { name: 'Earned + Remaining — manpower', src: 'Those Hrs ÷ the P6 calendar(s). The remaining side is the forecast crew — and it follows your Hiring / layoff overrides.' },
  { name: 'Hiring / layoff', src: 'This week’s manpower minus last week’s. Green = walk on, red = walk off. Double-click any future cell to type your own number (like the Excel); empty resets. Past weeks are history and locked.' },
  { name: 'Difference', src: '(Earned + Remaining manpower) − (Baseline manpower), per week. Plus = running or planning heavier than the baseline.' }
];

const CURVE_LEGEND = [
  { color: '#2563eb', label: 'Actual (earned) — blue, up to the data date' },
  { color: '#16a34a', label: 'Remaining / forecast — green, after the data date' },
  { color: '#9ca3af', label: 'Baseline up to the data date — gray' },
  { color: '#111827', label: 'Baseline after the data date — black' },
  { color: '#eab308', label: 'Data date — yellow line and yellow column' }
];

const SECTIONS = [
  {
    title: 'What lives where',
    body: [
      'The Staffing tab is the chart and the six tables. Peak men, data date, FAC, and chart controls live under Filters → Staffing plan. How the numbers are built, the colour key, and Hiring / layoff live in this Help (and Listen).',
      'Hours come from P6 per resource ID: Baseline schedule vs Current (Actual Units to the data date, then Remaining). Blue earned and green remaining meet on the data-date week. Manpower = Hrs ÷ each resource’s own P6 calendar. Double-click Hiring / layoff cells to override.'
    ]
  },
  {
    title: 'Where each number comes from',
    body: [
      'Everything is a straight P6 extract per resource ID — the same Resource Assignments the histogram reads. Hours tables are the raw units. Manpower tables divide by each resource’s own P6 calendar week hours (hover a resource name to see its calendar). What-if ranges in Filters override the calendar for a date span.',
      'At the start of a job, before anything is earned and before the schedule moves, Baseline and Earned + Remaining are naturally the same numbers. They drift apart as the Current schedule reschedules, earns, and takes extras — the Difference table is exactly that drift.'
    ]
  },
  {
    title: 'Hiring / layoff drives the forecast',
    body: [
      'Default hiring / layoff is calculated: this week’s manpower minus last week’s. Override a future cell (double-click) and the forecast manpower rebuilds from your numbers week by week — next week’s crew = this week’s crew + your hire / layoff. That is how the Excel staffing plans worked: the typed hiring row was the plan.',
      'Overridden cells turn amber. The strip chart, the Next-4-weeks card, the manpower forecast, and the Difference table all follow. Overrides are saved per job in this browser; Reset clears them. Hours tables stay pure P6 — overrides are a manpower plan, not a schedule edit.'
    ]
  },
  {
    title: 'The FAC formula and smoothing',
    body: [
      'Remaining listens to the FAC / ETC selector in Filters (Typical BAC ÷ CPI, Atypical, CPI×SPI, Schedule, pure P6 remaining…). The factor chip shows what is applied — factor 1.00 means untouched P6 remaining.',
      'Smooth forecast is optional and defaults to off (exact P6 extract). Moving it averages each remaining week ±N weeks, Excel AVERAGE style — edges shrink, zeros stay, and the window never crosses back over the data date.'
    ]
  },
  {
    title: 'Reading the page',
    body: [
      'Timescale has three layers like P6 — year, month, week-starting Sunday — and the same columns run through the chart, the hiring strip, and all six tables. The data-date column is yellow.',
      'The left panel (legend + Men axis) and the Resource column stay frozen while you scroll right. Click a table name to fold it. Two lines with the same trade are two P6 IDs — baseline vs extra (EX); all-zero rows in the window are hidden.',
      'Chart controls live under Filters → Staffing plan: Baseline schedule, Smooth forecast, the Past / Future / All window, and Curves vs Crew stack (crew grouping follows the Legend by slicer). Every other Filter pane slicer applies too — discipline, Resources, shop / field, baseline / extra, date range, activities. The Staffing tab has ⬇ xlsx (full week-by-week workbook) and ⬇ PDF (letterhead summary). Safety plan and Indirect extras have the same two buttons. The top ↓ CSV still downloads whichever tab is open.',
      'Safety staffing: tick Safety lines under Settings → Safety. Three rules — pick one. The original is 1 safety per N men (default 25). First-then: no safety under 20 men, the first safety at 20, then one more every 40. Three-step: first safety at 20, the next 40 men later (at 60), then one more every 50. Every number is editable. Red dashed lines mark the thresholds. Safety plan… stays on the tab you are on: Histogram uses the remaining bars you can count; Staffing uses that tab’s curve (hiring overrides). Weeks is the count of Sunday weeks over the line, not a cached Staffing series. ⬇ xlsx / ⬇ PDF are on the popup.',
      'Supervision: tick Plan under Filters → Supervision. Supervision is PER TRADE (P6 resource) — 1 foreman per 8 men within the trade (editable, overall default and per resource). GF is a floor rule: no GF under 24 men in a trade; the first GF starts at 24 men in ONE trade (8 × 3), the next at 48. "All trades" is the weekly sum of each trade\'s own needs — a GF is never shared across trades. Download xlsx or PDF, or email the PDF straight from the popup; a Supervision strip on this tab shows weekly FM / GF needed. Direct crew only — FMs and GFs ARE the indirects, computed from the direct men.',
      'Indirect extras: Settings → Safety → Indirect extras… is the BP-700-9000-1 GC and Self-Perform takeoff (Qty + Duration). The Excel is a 20-month construction schedule; Duration scales to this job. Empty Excel rows stay hidden. Override Qty or Months — that is the estimate base. Exports keep the vertical table and add dates as columns (same for Safety and Supervision). Supervision plan… stays on the histogram.',
      'Counts on bars: tick Counts on bars next to Man-hours / Man-weeks in Filters. On Staffing it prints each block’s men inside the Crew stack bars and the weekly crew total above the chart (curves view shows the totals too). On the Histogram it prints each colour’s count inside stacked Budget / Remaining bars (week total stays on top).',
      'Progress (under the tables, and on the Progress tab) is one column per weekly XER: that file’s earned ÷ that file’s budget. This week = this cumulative minus last week. Auto freezes past Sundays; a dated XER from the dropdown is as-of that file only.'
    ]
  }
];

export function StaffingHelpBody({ compact = false }) {
  return (
    <div style={{ display: 'grid', gap: compact ? 10 : 14 }}>
      <div style={{
        background: '#f0fdfa',
        border: '1px solid #99f6e4',
        borderRadius: 12,
        padding: compact ? '0.7rem 0.85rem' : '0.85rem 1rem'
      }}>
        <div style={{ fontWeight: 800, color: '#0f766e', marginBottom: 8, fontSize: compact ? '0.85rem' : '0.95rem' }}>
          The six tables
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? '0.75rem' : '0.8rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 8px 6px 0', color: '#64748b', fontWeight: 700, width: '30%' }}>Table</th>
              <th style={{ textAlign: 'left', padding: '4px 0 6px', color: '#64748b', fontWeight: 700 }}>Comes from</th>
            </tr>
          </thead>
          <tbody>
            {TABLE_MAP.map((row) => (
              <tr key={row.name}>
                <td style={{ padding: '5px 10px 5px 0', color: '#0f172a', fontWeight: 700, verticalAlign: 'top', borderTop: '1px solid #ccfbf1' }}>{row.name}</td>
                <td style={{ padding: '5px 0', color: '#334155', verticalAlign: 'top', borderTop: '1px solid #ccfbf1' }}>{row.src}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        padding: compact ? '0.7rem 0.85rem' : '0.85rem 1rem'
      }}>
        <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 6, fontSize: compact ? '0.85rem' : '0.95rem' }}>
          Curve colors
        </div>
        <div style={{ display: 'grid', gap: 4 }}>
          {CURVE_LEGEND.map((c) => (
            <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: compact ? '0.76rem' : '0.82rem', color: '#334155' }}>
              <span style={{ width: 18, height: 4, background: c.color, borderRadius: 2, flex: 'none' }} />
              {c.label}
            </div>
          ))}
        </div>
      </div>

      {SECTIONS.map((sec) => (
        <div key={sec.title}>
          <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 4, fontSize: compact ? '0.85rem' : '0.95rem' }}>
            {sec.title}
          </div>
          {sec.body.map((p) => (
            <p key={p.slice(0, 40)} style={{ margin: '0 0 6px', fontSize: compact ? '0.78rem' : '0.84rem', color: '#334155', lineHeight: 1.5 }}>
              {p}
            </p>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function StaffingPlanHelpModal({ onClose }) {
  if (!onClose) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Staffing Plan Help"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.25rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          width: 'min(800px, 100%)',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 30px 60px rgba(0,0,0,0.35)',
          border: `2px solid ${ACCENT}`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.35rem',
          background: `linear-gradient(135deg, ${ACCENT} 0%, #115e59 100%)`,
          color: 'white',
          flex: 'none'
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.15rem' }}>Staffing Plan Help</div>
            <div style={{ fontSize: '0.78rem', opacity: 0.95, marginTop: 2 }}>
              Baseline · Earned + Remaining · Hiring / layoff · Difference — straight P6 extracts
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton compact guideId={STAFFING_GUIDE_ID} title="Staffing Plan" accent={ACCENT} dark />
            <button
              type="button"
              onClick={onClose}
              style={{
                border: '2px solid rgba(255,255,255,0.5)',
                background: 'rgba(0,0,0,0.15)',
                color: 'white',
                borderRadius: 999,
                padding: '0.4rem 0.9rem',
                fontWeight: 800,
                cursor: 'pointer'
              }}
            >
              Done
            </button>
          </div>
        </div>
        <div style={{ padding: '1.15rem 1.35rem 1.4rem', overflow: 'auto' }}>
          <HelpWithVideoTour guideId={STAFFING_GUIDE_ID} title="Staffing Plan Help" accent={ACCENT}>
            <StaffingHelpBody />
          </HelpWithVideoTour>
        </div>
      </div>
    </div>
  );
}
